/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Modal,
  Tabs,
  Card,
  DatePicker,
  InputNumber,
  Typography,
  message,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { getModelPermission } from 'utils/permission';
import { DATE_FORMAT } from 'utils/normalizers';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO } from 'utils/constants';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { checkToken } from 'api/utils';
import { getPeriodicidadesDePagos } from 'api/catalogos/index';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import {
  deleteCalendarioDeNomina,
  getCalendarioDeNomina,
  getCalendariosDeNominas,
  getConfiguracionCalendario,
  getFuncionalidadesCalendario,
  patchCalendarioDeNomina,
  patchConfiguracionCalendario,
  postCalendarioDeNomina,
} from 'api/nominas/calendarioDeNominas';
import CalendarioDeNominasPeriodos, { calendarioDeNominaPeriodoPermission } from './CalendarioDeNominasPeriodos';

export const calendarioDeNominaPermission = {
  permissionModel: 'calendariodenomina',
  catalogsModels: ['periodofiscal', 'periodicidaddepago', 'percepcion', 'periododenominadetalle'],
  tabsModels: [],
};

const { Text } = Typography;

const campoFuncionalidad = (id) => `funcionalidad_${id}`;

// Campos requeridos por pestaña (ver CLAUDE.md, "Formularios con pestañas").
const requiredFormStructure = [
  {
    tabKey: 'general',
    tabFields: [
      'periodo_fiscal',
      'periodicidad_de_pago',
      'fecha_inicial',
      'fecha_final',
      'dias_habiles_de_bloqueo',
    ],
  },
];

const CalendarioDeNominas = ({ permission }) => {
  const [form] = Form.useForm();
  const [formConfiguracion] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [open, setOpen] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleConfiguracion, setVisibleConfiguracion] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [periodicidades, setPeriodicidades] = useState([]);
  const [funcionalidades, setFuncionalidades] = useState([]);

  const periodoPermission = getModelPermission(
    calendarioDeNominaPeriodoPermission.permissionModel,
    calendarioDeNominaPeriodoPermission.catalogsModels,
    calendarioDeNominaPeriodoPermission.tabsModels,
    false,
  );

  const fetchData = async () => {
    const _data = await getCalendariosDeNominas();
    setData(formatReceived(_data));
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_data, _periodosFiscales, _periodicidades, _funcionalidades] = await Promise.all([
        getCalendariosDeNominas(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodicidadesDePagos(),
        getFuncionalidadesCalendario(),
      ]);
      if (mounted) {
        setData(formatReceived(_data));
        setPeriodosFiscales(_periodosFiscales?.results || _periodosFiscales || []);
        setPeriodicidades(_periodicidades?.results || _periodicidades || []);
        setFuncionalidades(_funcionalidades);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const valoresIniciales = () => ({
    estados_globales: ESTATUS_ACTIVO,
    ...Object.fromEntries(funcionalidades.map((f) => [campoFuncionalidad(f.id), false])),
  });

  const onCancel = () => {
    setOpen(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    setCurrentTabKey('general');
    form.resetFields();
  };

  const cargarEnFormulario = (calendario) => {
    form.resetFields();
    const cerradas = calendario.funcionalidades_cerradas || [];
    form.setFieldsValue({
      ...formatReceived(calendario),
      ...Object.fromEntries(funcionalidades
        .map((f) => [campoFuncionalidad(f.id), cerradas.includes(f.id)])),
    });
  };

  const onClickAdd = async () => {
    onCancel();
    setLoading(true);
    // Los días de bloqueo se proponen con la configuración general;
    // se pueden ajustar por calendario.
    const configuracion = await getConfiguracionCalendario();
    form.setFieldsValue({
      ...valoresIniciales(),
      dias_habiles_de_bloqueo: configuracion?.dias_habiles_para_cierre || 5,
    });
    setOpen(true);
    setLoading(false);
  };

  const onClickEdit = async (registro = null) => {
    setLoading(true);
    const id = registro?.id || selectedRowKeys[0];
    const calendario = await getCalendarioDeNomina(id);
    if (calendario) {
      setSelectedRowKeys([calendario.id]);
      setSelectedData(calendario);
      cargarEnFormulario(calendario);
      setOpen(true);
    }
    setLoading(false);
  };

  const onValidationError = (err) => {
    const campos = (err?.errorFields || []).map((e) => e.name[0]);
    const tab = requiredFormStructure.find((t) => t.tabFields.some((f) => campos.includes(f)));
    if (tab) setCurrentTabKey(tab.tabKey);
    message.warn('Favor de corregir los errores señalados');
  };

  // FormSubmitControls llama onFinish(false) para "Guardar"
  // y onFinish(true) para "Guardar y Continuar".
  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const payload = {
        periodo_fiscal: values.periodo_fiscal,
        periodicidad_de_pago: values.periodicidad_de_pago,
        fecha_inicial: values.fecha_inicial?.format(DATE_FORMAT),
        fecha_final: values.fecha_final?.format(DATE_FORMAT),
        dias_habiles_de_bloqueo: values.dias_habiles_de_bloqueo,
        funcionalidades_cerradas: funcionalidades
          .filter((f) => values[campoFuncionalidad(f.id)])
          .map((f) => f.id),
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchCalendarioDeNomina(key, payload)
        : postCalendarioDeNomina(payload));
      if (saved) {
        await fetchData();
        if (_continue || !key) {
          // Al agregar se queda en edición para poder generar los periodos.
          await onClickEdit(saved);
        } else {
          onCancel();
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err?.errorFields) {
        onValidationError(err);
      } else {
        onError(err, setLoading, [form]);
      }
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const deleted = await deleteCalendarioDeNomina(selectedRowKeys[0]);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const onChangePeriodoFiscal = (id) => {
    const periodoFiscal = periodosFiscales.find((p) => p.id === id);
    if (periodoFiscal) {
      const { fecha_inicial: inicial, fecha_final: final } = periodoFiscal;
      form.setFieldsValue({
        fecha_inicial: inicial ? moment(inicial) : undefined,
        fecha_final: final ? moment(final) : undefined,
      });
    }
  };

  const onOpenConfiguracion = async () => {
    setLoading(true);
    const configuracion = await getConfiguracionCalendario();
    if (configuracion) {
      formConfiguracion.setFieldsValue(configuracion);
      setVisibleConfiguracion(true);
    }
    setLoading(false);
  };

  const onSaveConfiguracion = async () => {
    try {
      const values = await formConfiguracion.validateFields();
      setLoading(true);
      const saved = await patchConfiguracionCalendario(values);
      if (saved) setVisibleConfiguracion(false);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [formConfiguracion]);
    }
  };

  const columns = [
    {
      titleText: 'Ejercicio',
      dataIndex: 'periodo_fiscal_periodo',
      key: 'periodo_fiscal_periodo',
      width: 150,
    },
    {
      titleText: 'Periodicidad',
      dataIndex: 'periodicidad_de_pago_descripcion',
      key: 'periodicidad_de_pago_descripcion',
      width: 200,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => val?.format?.('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 150,
      render: (val) => val?.format?.('DD/MM/YYYY'),
    },
    {
      titleText: 'No. de Periodos',
      dataIndex: 'numero_de_periodos',
      key: 'numero_de_periodos',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales_descripcion',
      key: 'estados_globales_descripcion',
      width: 150,
    },
  ];

  const requiredRule = [{ required: true, message: 'El campo es requerido' }];
  const esEdicion = selectedRowKeys.length > 0;

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={{ selectedRowKeys, type: 'radio' }}
            handleOnRowClick={(record) => {
              setSelectedRowKeys([record.id]);
              setSelectedData(record);
            }}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete: () => setVisibleAlert(true),
            }}
            customActions={[
              {
                onClick: onOpenConfiguracion,
                icon: <SettingOutlined />,
                text: 'Configuración',
                disabledBySelection: false,
                disabled: !permission?.view,
              },
            ]}
            permission={permission}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container"
            title={`${esEdicion ? 'Editar' : 'Agregar'} Calendario de Nóminas`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar y Continuar"
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              scrollToFirstError
              name="calendario_de_nomina"
              initialValues={valoresIniciales()}
            >
              <Tabs
                type="card"
                activeKey={currentTabKey}
                onChange={setCurrentTabKey}
                items={[
                  {
                    key: 'general',
                    label: 'Calendario de Nóminas',
                    forceRender: true,
                    children: (
                      <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item name="periodo_fiscal" label="Ejercicio" rules={requiredRule} hasFeedback>
                            <Select
                              labelProp="periodo"
                              keyProp="id"
                              dataSource={periodosFiscales}
                              disabled={esEdicion}
                              onChange={onChangePeriodoFiscal}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item name="periodicidad_de_pago" label="Periodicidad" rules={requiredRule} hasFeedback>
                            <Select
                              labelProp="descripcion"
                              keyProp="id"
                              dataSource={periodicidades}
                              disabled={esEdicion}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                          <Form.Item name="fecha_inicial" label="Fecha Inicial" rules={requiredRule}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                          <Form.Item name="fecha_final" label="Fecha Final" rules={requiredRule}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            name="dias_habiles_de_bloqueo"
                            label="Días Hábiles de Bloqueo Antes del Pago"
                            rules={[
                              ...requiredRule,
                              {
                                type: 'number',
                                min: 1,
                                max: 60,
                                message: 'Capture un valor entre 1 y 60',
                              },
                            ]}
                          >
                            <InputNumber min={1} max={60} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                          <Form.Item name="estados_globales" label="Estatus">
                            <Select activeInactive disabled />
                          </Form.Item>
                        </Col>
                        {!esEdicion && (
                          <Col span={24}>
                            <Text type="secondary">
                              Al agregar un nuevo calendario, el calendario activo anterior
                              de la misma Periodicidad cambia a Estatus Inactivo.
                            </Text>
                          </Col>
                        )}
                      </Row>
                    ),
                  },
                  {
                    key: 'periodos',
                    label: 'Periodos',
                    disabled: !esEdicion,
                    children: (
                      <CalendarioDeNominasPeriodos
                        calendarioId={selectedRowKeys[0]}
                        calendarioActivo={selectedData?.estados_globales === ESTATUS_ACTIVO}
                        permission={periodoPermission}
                      />
                    ),
                  },
                  {
                    key: 'funcionalidades',
                    label: 'Funcionalidades Que Se Cierran',
                    forceRender: true,
                    children: (
                      <>
                        <Text type="secondary">
                          Las funcionalidades marcadas con &quot;Sí&quot; quedan en solo lectura
                          desde la Fecha de Cierre hasta la Fecha de Pago (inclusive) de cada
                          periodo, salvo que el periodo se abra manualmente.
                        </Text>
                        <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
                          {funcionalidades.map((funcionalidad) => (
                            <Col xs={24} sm={12} md={8} key={funcionalidad.id}>
                              <Form.Item
                                name={campoFuncionalidad(funcionalidad.id)}
                                label={funcionalidad.descripcion}
                              >
                                <Select trueFalse />
                              </Form.Item>
                            </Col>
                          ))}
                        </Row>
                      </>
                    ),
                  },
                ]}
              />
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`el Calendario de Nóminas del ejercicio ${selectedData?.periodo_fiscal_periodo || ''} ${
            selectedData?.periodicidad_de_pago_descripcion || ''} y todos sus periodos`}
          loading={loading}
        />
        <Modal
          title="Configuración del Calendario de Nóminas"
          open={visibleConfiguracion}
          onOk={onSaveConfiguracion}
          onCancel={() => setVisibleConfiguracion(false)}
          okText="Guardar"
          cancelText="Regresar"
          okButtonProps={{ disabled: !permission?.change }}
          confirmLoading={loading}
        >
          <Form layout="vertical" form={formConfiguracion}>
            <Form.Item
              name="dias_habiles_para_cierre"
              label="Días Hábiles de Bloqueo Antes del Pago"
              extra="Valor que se propone al agregar un calendario. Fecha de Cierre = Fecha de Pago menos estos días hábiles (lunes a viernes, sin días festivos)."
              rules={[
                ...requiredRule,
                {
                  type: 'number',
                  min: 1,
                  max: 60,
                  message: 'Capture un valor entre 1 y 60',
                },
              ]}
            >
              <InputNumber min={1} max={60} style={{ width: '100%' }} disabled={!permission?.change} />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </Row>
  );
};

export default CalendarioDeNominas;
